import { UBFF_FILTER_KEYS_MAP } from './hotel-detail-price';

import type { PriceContext } from './types';
import type {
  Feature,
  Offer,
  Partner,
  Tag,
  PartnerOffers,
  Filter,
  OptionKey,
  OfferFilter,
} from '@shared/types/frontend/ubff/hotel-detail-price';
import type { OfferFilters } from '@shared/types/url-params';
import type { Nullable } from '@shared/types/utils';
import type { TaxAndFeeInclusionPolicy } from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/common.pb';

import {
  PRICE_TYPE_TOTAL,
  TAX_AND_FEE_INCLUSION_POLICY_FEES_INCLUDED,
  TAX_AND_FEE_INCLUSION_POLICY_TAXES_FEES_INCLUDED,
} from '@shared/constants/ubff-gateway';

const DISPLAY_ORDER = [
  'FEATURE_TYPE_CANCELLATION_POLICY',
  'FEATURE_TYPE_MEAL_PLAN',
];

const SEO_PAGE_NAME = 'seo_hotel_details';
const DETAILS_PAGE_NAME = 'hotels_details';

export const getNextRequestFilters = ({
  clickedOptionKeys,
  prevResponseFilters,
}: {
  clickedOptionKeys: string[];
  prevResponseFilters: OfferFilter[];
}): Filter[] => {
  const nextRequestFilters: Filter[] = [];

  const getNextSelectedOptions = (filter: OfferFilter) => {
    const selectedOptions: string[] = [];
    filter.options.forEach((option) => {
      // We only need the selected options inside the request.
      if (clickedOptionKeys.includes(option.option)) {
        if (!option.selected) {
          // The clicked option is not selected before, so we should select it;
          selectedOptions.push(option.option);
        }
      } else if (option.selected) {
        // For other options in the prev response, keep the selected ones.
        selectedOptions.push(option.option);
      }
    });
    return selectedOptions;
  };

  prevResponseFilters.forEach((filter) => {
    const selectedOptions = getNextSelectedOptions(filter);
    if (selectedOptions.length > 0) {
      nextRequestFilters.push({
        key: filter.key,
        options: selectedOptions,
      });
    }
  });
  return nextRequestFilters;
};

export const getFilterParamsToUpdateUrl = (
  requestFilters?: Filter[],
): Nullable<OfferFilters> => {
  if (!requestFilters || requestFilters.length === 0) return null;
  return requestFilters
    .map((filter) => {
      const urlParam: OfferFilters = {};
      urlParam[filter.key] = filter.options;
      return urlParam;
    })
    .reduce((acc: OfferFilters, cur: OfferFilters) => ({ ...acc, ...cur }), {});
};

export const transformFiltersUrlParam = (
  urlParams: Nullable<OfferFilters>,
): Filter[] => {
  if (!urlParams) return [];
  return Object.entries(urlParams).map(([key, options]) => ({
    /*
    The key is input from browser (URL), and we have no way to validate it is a valid OptionKey.
    Because we aim to not hard code UBFF keys.
    We have confirmed that it's OK to pass non-OptionKey keys to UBFF,
    so we just pass it as it is.
     */
    key: key as OptionKey,
    options,
  }));
};

export const getPriceSummaryKey = ({
  priceType,
  taxAndFeeInclusionPolicy,
}: {
  taxAndFeeInclusionPolicy?: TaxAndFeeInclusionPolicy;
  priceType?: Nullable<string>;
}) => {
  if (
    taxAndFeeInclusionPolicy ===
      TAX_AND_FEE_INCLUSION_POLICY_TAXES_FEES_INCLUDED ||
    priceType === PRICE_TYPE_TOTAL
  ) {
    return 'OTARates_TaxesIncluded';
  }
  if (taxAndFeeInclusionPolicy === TAX_AND_FEE_INCLUSION_POLICY_FEES_INCLUDED) {
    return 'OTARates_TaxesExcluded_Only';
  }
  return 'dayview_taxes_not_included';
};

export const refactorFeatures = (featureList: Feature[]) => {
  const showAmenities: Feature[] = [];
  DISPLAY_ORDER.forEach((displayKey) => {
    const item = featureList.find(
      (feature) => displayKey === feature.featureType,
    );
    if (item && item.title) {
      showAmenities.push(item);
    }
  });

  return showAmenities;
};

export const isPositiveFeature = ({ baselineDelta }: Feature) =>
  baselineDelta === 'BASELINE_DELTA_POSITIVE';

export const buildPositiveFeatures = (features: Feature[]) =>
  features.filter((feature) => isPositiveFeature(feature));

export const hasGreatPriceTag = (tags: Tag[]) =>
  tags.some((tag) => tag.tagType === 'TAG_TYPE_GREAT_PRICE');

export const getGreatPriceTag = (tags: Tag[]) =>
  tags.find((tag) => tag.tagType === 'TAG_TYPE_GREAT_PRICE');

export const getVisibleCugLabelTag = (tags: Tag[]) =>
  tags.find(
    (tag) =>
      tag.tagType &&
      ['TAG_TYPE_LOGGED_IN', 'TAG_TYPE_MOBILE'].indexOf(tag.tagType) !== -1,
  );

export const getCugTag = (tags: Tag[]) =>
  tags.find(
    (tag) =>
      tag.tagType &&
      [
        'TAG_TYPE_GREAT_PRICE',
        'TAG_TYPE_CHEAPEST_FOR_TRAVELLER',
        'TAG_TYPE_CHEAPEST_FOR_HOTEL',
      ].indexOf(tag.tagType) === -1,
  );

export const hasCheapestForTravellerTag = (tags: Tag[]) =>
  tags.some((tag) => tag.tagType === 'TAG_TYPE_CHEAPEST_FOR_TRAVELLER');

export const hasCheapestForHotelTag = (tags: Tag[]) =>
  tags.some((tag) => tag.tagType === 'TAG_TYPE_CHEAPEST_FOR_HOTEL');

export const hasBookingGeniusTag = (tags: Tag[]) =>
  tags.some((tag) => tag.tagType === 'TAG_TYPE_BOOKING_GENIUS');

export const buildRoomTypes = (roomFilters?: OfferFilter[]) => {
  if (!roomFilters) {
    return undefined;
  }
  const roomTypeFilters = roomFilters.filter(
    ({ key }) => key === UBFF_FILTER_KEYS_MAP.OPTION_KEY_ROOM_TYPE,
  );
  if (roomTypeFilters.length === 0) {
    return undefined;
  }
  return roomTypeFilters
    .flatMap((filter) => filter.options)
    .map((optionObject) => optionObject.option)
    .join(',');
};

export const buildOfferEventProps = ({
  offer,
  partner,
}: {
  offer: Offer;
  partner?: Partner;
}) => {
  const {
    features,
    partner: partnerInOffer,
    price: { basicPrice, discountInfo, price, tags, taxesAndFees },
    roomName,
    roomType,
    tags: dealsTags,
  } = offer;

  const { id: partnerId } = partner || partnerInOffer || {};

  const positiveAmenitiesEvent = buildPositiveFeatures(features).reduce(
    (acc: string[], cur) => [...acc, cur.value],
    [],
  );

  const isLowest = hasCheapestForTravellerTag(tags);
  const isLowestForHotel = hasCheapestForHotelTag(tags);
  const isGreatPrice = hasGreatPriceTag(tags);

  return {
    partnerId,
    isDbook: false,
    isGreatPrice,
    isLowest,
    isLowestForHotel,
    price,
    basePrice: basicPrice,
    taxFees: taxesAndFees?.totalTaxes,
    positiveAmenities:
      positiveAmenitiesEvent.length > 0
        ? positiveAmenitiesEvent.join(',')
        : undefined,
    cugType: discountInfo?.discountType,
    discountPercentage: discountInfo?.percentage,
    roomName,
    roomType,
    tags: dealsTags,
  };
};

export const buildEventCommonProps = ({
  currency,
  isAmenityAreaAvailable,
  isCugAreaAvailable,
  offer,
  partner,
  partnerRanking,
  priceContext,
}: {
  offer: Offer;
  partner?: Partner;
  priceContext: PriceContext;
  partnerRanking: number;
  currency: string;
  isCugAreaAvailable: boolean;
  isAmenityAreaAvailable: boolean;
}) => {
  const {
    correlationId,
    dayViewCorrelationId,
    hotelId,
    isFiltered,
    requestId,
    searchId,
  } = priceContext;

  const offerProps = buildOfferEventProps({ offer, partner });

  return {
    ...offerProps,
    hotelId,
    isFiltered: !!isFiltered,
    searchId,
    requestId,
    correlationId,
    currency,
    partnerRanking,
    isAmenityAreaAvailable,
    isCugAreaAvailable,
    dayviewCorrelationId: dayViewCorrelationId,
  };
};

export const buildPriceSeenEventProps = ({
  currency,
  isAmenityAreaAvailable,
  isCugAreaAvailable,
  offer,
  panelPosition,
  partner,
  partnerRanking,
  priceContext,
}: {
  offer: Offer;
  partner?: Partner;
  priceContext: PriceContext;
  partnerRanking: number;
  currency: string;
  isCugAreaAvailable: boolean;
  isAmenityAreaAvailable: boolean;
  panelPosition: string;
}) => {
  const commonProps = buildEventCommonProps({
    currency,
    isAmenityAreaAvailable,
    isCugAreaAvailable,
    offer,
    partner,
    partnerRanking,
    priceContext,
  });

  return {
    ...commonProps,
    panelPosition,
  };
};

export const buildPriceSelectedEventProps = ({
  clickArea,
  currency,
  hasRedirect,
  isAmenityAreaAvailable,
  isCugAreaAvailable,
  offer,
  offerPosition,
  partner,
  partnerRanking,
  position,
  priceContext,
}: {
  offer: Offer;
  partner?: Partner;
  priceContext: PriceContext;
  partnerRanking: number;
  currency: string;
  clickArea: string;
  hasRedirect: boolean;
  isCugAreaAvailable: boolean;
  isAmenityAreaAvailable: boolean;
  offerPosition?: number;
  position?: number;
}) => {
  const { filters, isSeoTraffic, searchId } = priceContext;

  const commonProps = buildEventCommonProps({
    currency,
    isAmenityAreaAvailable,
    isCugAreaAvailable,
    offer,
    partner,
    partnerRanking,
    priceContext,
  });

  return {
    ...commonProps,
    searchCycleId: searchId,
    rateFilterValues: filters?.flatMap((filter) => filter.options).join(','),
    clickArea,
    hasRedirect,
    position,
    offerPosition,
    pageName: isSeoTraffic ? SEO_PAGE_NAME : DETAILS_PAGE_NAME,
  };
};

export const buildPricesLoadedEventProps = ({
  currency,
  partnersOffersList,
  priceContext,
  roomFilters,
}: {
  currency: string;
  priceContext: PriceContext;
  roomFilters?: OfferFilter[];
  partnersOffersList: PartnerOffers[];
}) => {
  const {
    correlationId,
    dayViewCorrelationId: dayviewCorrelationId,
    hotelId,
    isFiltered,
    isSeoTraffic,
    requestId,
    searchId,
    stay,
  } = priceContext;

  const roomTypes = buildRoomTypes(roomFilters);
  const [firstPartnerOffer] = partnersOffersList;
  const { offers: partnerOffers, partner } = firstPartnerOffer;

  const { discountInfo } = partnerOffers[0].price;
  const { price } = partnerOffers[0].price;
  const partnerId = partner.id;

  const allRatesCount = partnersOffersList.flatMap(
    (offersOfPartner) => offersOfPartner.offers,
  ).length;

  return {
    hotelId,
    requestId,
    searchId,
    correlationId,
    dayviewCorrelationId,
    hasCoupon: false,
    isFiltered,
    hasMeta: true,
    hasDbook: false,
    dBook: false,
    allRatesCount,
    currency,
    roomTypes,
    layoutType: 'ONLY_META',
    cugType: discountInfo?.discountType,
    partnerId,
    price,
    stay,
    isCugDeal: !!discountInfo?.discountType,
    pageName: isSeoTraffic ? SEO_PAGE_NAME : DETAILS_PAGE_NAME,
    ratesWithImageCount: 0,
  };
};
